import { t } from 'i18next';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { ChuteSchedulerActions } from '@/app/slices/chuteScheduler';
import { addToast } from '@/app/slices/toast';
import { Button } from '@/components/ui/button';
import { ChuteScheduleRow } from '@/rtk-query.gql';

import {
  buildTimeMap,
  groupBySite,
  numberPerSite,
  overflowWithChutes,
  overflowWithSequence,
  sanitizeRows,
} from '../utils/scheduleMath';

type Props = { className?: string };

export const ApplyButton = ({ className }: Props) => {
  const dispatch = useDispatch();

  const {
    schedulingRows,
    selectedSchedulingRows,
    startTime,
    interval,
    multiChuteCount,
  } = useAppSelector((s) => s.chuteScheduler);

  const hasSelection = selectedSchedulingRows.some(Boolean);
  const baseRows = hasSelection ? selectedSchedulingRows : schedulingRows;

  const target = sanitizeRows(baseRows);

  const siteGroups = groupBySite(target);
  const largestSite = Math.max(0, ...siteGroups.map((group) => group.length));
  const startNum = Number(startTime);

  const spills =
    multiChuteCount > 1
      ? overflowWithChutes(largestSite, multiChuteCount, startNum, interval)
      : overflowWithSequence(largestSite, startNum, interval);

  const persistAndResequence = (map: Map<string, string>) => {
    const updated = sanitizeRows(schedulingRows).map((r) => ({
      ...r,
      aspTime: map.get(r.aspirationId) ?? r.aspTime,
    }));

    const byTime = (a: ChuteScheduleRow, b: ChuteScheduleRow) => {
      const aNum = a.aspTime ? Number(a.aspTime) : Infinity;
      const bNum = b.aspTime ? Number(b.aspTime) : Infinity;
      return aNum - bNum;
    };

    const resequenced = numberPerSite(
      groupBySite(updated).flatMap((group) => [...group].sort(byTime)),
    );

    dispatch({
      type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
      rows: resequenced,
    });

    const selectedIds = new Set(
      sanitizeRows(selectedSchedulingRows).map((r) => r.aspirationId),
    );
    dispatch({
      type: ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS,
      rows: resequenced.filter((r) => selectedIds.has(r.aspirationId)),
    });
  };

  const onClick = () => {
    if (spills) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Schedule exceeds one day',
          detail:
            'Increase the number of chutes or lower the interval, then try again.',
          life: 6000,
        }),
      );
      return;
    }

    const map = new Map<string, string>();
    siteGroups.forEach((group) =>
      buildTimeMap(
        group.map((r) => r.aspirationId!),
        startNum,
        interval,
        multiChuteCount,
      ).forEach((time, id) => map.set(id, time)),
    );
    persistAndResequence(map);
  };

  const allCnt = sanitizeRows(schedulingRows).length;
  const selCnt = sanitizeRows(selectedSchedulingRows).length;

  return (
    <Button
      variant={selCnt === allCnt ? 'default' : 'secondary'}
      className={`gap-1.5 ${className ?? ''}`.trim()}
      onClick={onClick}
    >
      <i className="pi pi-clock text-xs" />
      {selCnt === allCnt
        ? t('chuteScheduler.applyToAllRows')
        : t('chuteScheduler.applyToSelectedRows')}
    </Button>
  );
};
